import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Activity } from './activity';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { ParameterConfig } from './parameter-config';
import { ParameterConfigUtils } from '../adapters/linkedservices/parameter-config.util';
import { PbiNotebook } from '../notebook/pbiNotebook';
import { format } from '../../shared/utils/string.util';
import { ParameterValue } from '../base/parameter-value';
import { FabricNotebookLinkedService } from '../linkedservice/fabric-notebook.linkedservice';
import { LinkedService } from '../linkedservice/linkedservice';
import { Copier } from '../base/copier';
import { GlobalVariables } from '../../constants/globals';

@Metamodel.model({
    name: ModelConstants.tridentNotebookActivity,
    referable: true
})
export class TridentNotebookActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly NOTEBOOKID: IAttribute;
    public static readonly WORKSPACEID: IAttribute;
    public static readonly SESSIONTAG: IAttribute;
    public static readonly SPARK_DRIVER_CORE: IAttribute;
    public static readonly SPARK_DRIVER_MEMORY: IAttribute;
    public static readonly SPARK_EXECUTOR_CORE: IAttribute;
    public static readonly SPARK_EXECUTOR_MEMORY: IAttribute;
    public static readonly DYNAMIC_ALLOCATION: IAttribute;
    public static readonly SPARK_EXECUTOR_INSTANCES: IAttribute;
    public static readonly MIN_EXECUTORS: IAttribute;
    public static readonly MAX_EXECUTORS: IAttribute;
    public static readonly SPARK_PROPERTIES: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;
    public static readonly SHOW_ENRICHED_OUTPUT: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: FabricNotebookLinkedService.MODEL,
        constraint: {
            validator: TridentNotebookActivity.validateConnection
        }
    })
    public linkedService?: FabricNotebookLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: shellStringResources.NotebookActivityLabel,
        reference: true
    })
    public notebook?: PbiNotebook;

    @Metamodel.attribute({
        display: shellStringResources.NotebookActivityLabel,
        association: 'NOTEBOOKID',
        constraint: {
            validator: TridentNotebookActivity.validateNotebookId
        },
        templatable: true
    })
    public notebookId?: string;

    @Metamodel.attribute({
        display: shellStringResources.TridentWorkspaceLabel,
        association: 'WORKSPACEID',
        constraint: {
            validator: TridentNotebookActivity.validateWorkspaceId
        },
        templatable: true
    })
    public workspaceId?: string;

    @Metamodel.attribute({
        display: shellStringResources.TridentNotebookSessionTagLabel,
        association: 'SESSIONTAG',
        templatable: true
    })
    public sessionTag?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverCores,
        association: 'SPARK_DRIVER_CORE',
        templatable: true
    })
    public sparkDriverCore?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesDriverMemory,
        association: 'SPARK_DRIVER_MEMORY',
        templatable: true
    })
    public sparkDriverMemory?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorCores,
        association: 'SPARK_EXECUTOR_CORE',
        templatable: true
    })
    public sparkExecutorCore?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesExecutorMemory,
        association: 'SPARK_EXECUTOR_MEMORY',
        templatable: true
    })
    public sparkExecutorMemory?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityDynamicExecutorLabel,
        association: 'DYNAMIC_ALLOCATION',
        templatable: true
    })
    public dynamicAllocation?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobPropertiesNumExecutors,
        association: 'SPARK_EXECUTOR_INSTANCES',
        templatable: true
    })
    public sparkExecutorInstances?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMinExecutorsLabel,
        association: 'MIN_EXECUTORS',
        templatable: true
    })
    public minExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMaxExecutorsLabel,
        association: 'MAX_EXECUTORS',
        templatable: true
    })
    public maxExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.TridentSparkPropertiesLabel,
        association: 'SPARK_PROPERTIES'
    })
    public sparkProperties?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: shellStringResources.NotebookParametersLabel,
        constraint: {
            validator: ParameterConfigUtils.createParameterConfigurationValidator(true, null, false)
        }
    })
    public parameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        association: 'SHOW_ENRICHED_OUTPUT',
        templatable: true
    })
    public showEnrichedOutput?: boolean;

    public isConnectionRequired: boolean = false;

    private static validateWorkspaceId(_tridentNotebookActivity?: TridentNotebookActivity, workspaceId?: string): IViolation[] | undefined {
        if (!workspaceId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.TridentWorkspaceIdLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateNotebookId(_tridentNotebookActivity?: TridentNotebookActivity, notebookId?: string): IViolation[] | undefined {
        if (!notebookId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.TridentNotebookActivityNotebookIdLabel)
                }
            ];
        }
        return undefined;
    }

    private static validateConnection(_tridentNotebookActivity?: TridentNotebookActivity, linkedService?: LinkedService): IViolation[] | undefined {
        if (_tridentNotebookActivity.isConnectionRequired && !linkedService) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.ConnectionLabel)
                }
            ];
        } else if (linkedService && linkedService.getLinkedServiceType() != Metamodel.modelFor(FabricNotebookLinkedService).name) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.ActivityNotSupportLinkedServiceError,
                            format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.NotebookActivityLabel),
                            format(shellStringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(linkedService).name)
                        ),
                    messageType: 'unsupported'
                }
            ];
        }
        return undefined;
    }
    public onActivityCloned(clonedActivity: TridentNotebookActivity, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as FabricNotebookLinkedService;
            }
        }
    }
}
